import { useState, useEffect, useMemo, useRef } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import {
  Loader2, RefreshCw, Users, Trophy, TrendingUp, TrendingDown, AlertCircle,
  Calendar, Target, Flame, ClipboardList, Wallet, Award, Camera,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell,
} from "recharts";

const CURRENT_FY_START = (() => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
})();
const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(-2)}`;

const fmtL = (v) => {
  if (!v && v !== 0) return "0";
  const l = v / 100000;
  if (Math.abs(l) >= 100) return `${Math.round(l).toLocaleString("en-IN")} L`;
  if (Math.abs(l) >= 10) return `${l.toFixed(1)} L`;
  return `${l.toFixed(2)} L`;
};

function Kpi({ label, value, sub, color, icon: Icon }) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
          <p className="text-2xl font-bold mt-1 truncate" style={{ color }}>{value}</p>
          {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}><Icon className="w-4 h-4" style={{ color }} /></div>}
      </div>
    </CardContent></Card>
  );
}

export default function SalesTeamDashboard() {
  const [view, setView] = useState("fy"); // "fy" | "monthly" | "quarterly"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fyYear, setFyYear] = useState(CURRENT_FY_START);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collections/sales-team/analytics?fy_start_year=${fyYear}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyYear]);

  const perfChart = useMemo(() => {
    if (!data) return [];
    return data.members.map((m) => ({
      name: m.name,
      Collected: m.collected,
      Target: m.target,
      YtdTarget: m.ytd_target,
    }));
  }, [data]);

  const activeMembers = data?.members?.filter(m => m.active !== false) || [];
  const top = data?.leaderboard?.find(m => m.collected > 0);

  return (
    <div className="space-y-4" data-testid="sales-team-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-[#0066CC]" /> Sales Team Performance
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Per-person collection tracking · {data ? data.fy_label : fyLabel(fyYear)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border overflow-hidden" data-testid="sales-view-toggle">
            <button onClick={() => setView("fy")} className={`px-3 py-1.5 text-xs font-semibold ${view === "fy" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`} data-testid="sales-view-fy">FY View</button>
            <button onClick={() => setView("monthly")} className={`px-3 py-1.5 text-xs font-semibold ${view === "monthly" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`} data-testid="sales-view-monthly">Monthly</button>
            <button onClick={() => setView("quarterly")} className={`px-3 py-1.5 text-xs font-semibold ${view === "quarterly" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`} data-testid="sales-view-quarterly">Quarterly</button>
          </div>
          {view === "fy" && (
            <select value={fyYear} onChange={(e) => setFyYear(Number(e.target.value))} className="h-9 border border-slate-200 rounded-md px-2 text-xs bg-white" data-testid="sales-team-fy-select">
              {[CURRENT_FY_START - 2, CURRENT_FY_START - 1, CURRENT_FY_START, CURRENT_FY_START + 1].map(y => (
                <option key={y} value={y}>{fyLabel(y)}</option>
              ))}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sales-team-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {view === "monthly" ? <MonthlyDashboard /> : view === "quarterly" ? <QuarterlyDashboard /> : loading && !data ? (
        <Card><CardContent className="p-12 flex flex-col items-center justify-center text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-2" />
          <p className="text-sm">Loading team performance…</p>
        </CardContent></Card>
      ) : data && activeMembers.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-400">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
          <p className="text-sm">No sales members yet.</p>
          <p className="text-xs mt-1">Add them under Settings → Sales Team.</p>
        </CardContent></Card>
      ) : data ? (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Team Target" value={`₹${fmtL(data.totals.target)}`} sub={data.fy_label} color="#0066CC" icon={Users} />
            <Kpi label="Team Collected" value={`₹${fmtL(data.totals.collected)}`} sub={`${data.totals.achievement_pct}% of target`} color="#059669" icon={TrendingUp} />
            <Kpi
              label="Top Performer"
              value={top ? top.name : "—"}
              sub={top ? `₹${fmtL(top.collected)} · ${top.achievement_pct}%` : ""}
              color="#7C3AED"
              icon={Trophy}
            />
            <Kpi
              label="Unassigned"
              value={`₹${fmtL(data.unassigned.collected)}`}
              sub={`${data.unassigned.receipt_count} receipt${data.unassigned.receipt_count === 1 ? "" : "s"} — reassign in Settings`}
              color={data.unassigned.collected > 0 ? "#D97706" : "#94A3B8"}
              icon={AlertCircle}
            />
          </div>

          {/* Person-wise Performance bar chart */}
          <Card>
            <CardContent className="p-5">
              <div className="mb-3">
                <h3 className="font-bold text-slate-900 text-sm">Collections vs Target — by Sales Person</h3>
                <p className="text-[11px] text-slate-500">Blue = Actual collected · Grey = Annual target · Dashed = YTD pro-rated target</p>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={perfChart} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtL(v)} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
                          <p className="font-semibold text-slate-700 mb-1">{label}</p>
                          <p>Collected: <span className="text-[#0066CC] font-semibold">₹{fmtL(p.Collected)}</span></p>
                          <p>Annual Target: <span className="text-slate-500 font-semibold">₹{fmtL(p.Target)}</span></p>
                          <p>YTD Target: <span className="text-slate-500 font-semibold">₹{fmtL(p.YtdTarget)}</span></p>
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Target" fill="#CBD5E1" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Collected" fill="#0066CC" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Leaderboard table */}
          <Card>
            <CardContent className="p-5">
              <h3 className="font-bold text-slate-900 text-sm mb-3">Leaderboard — {data.fy_label}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="sales-leaderboard-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                      <th className="text-left py-2 pr-3 font-semibold">Rank</th>
                      <th className="text-left py-2 pr-3 font-semibold">Sales Person</th>
                      <th className="text-right py-2 pr-3 font-semibold">Target</th>
                      <th className="text-right py-2 pr-3 font-semibold">Collected</th>
                      <th className="text-right py-2 pr-3 font-semibold">Achievement</th>
                      <th className="text-right py-2 pr-3 font-semibold">YTD Eff.</th>
                      <th className="text-right py-2 pr-3 font-semibold">Share</th>
                      <th className="text-right py-2 pr-3 font-semibold">Receipts</th>
                      <th className="text-right py-2 pr-3 font-semibold">Avg Ticket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leaderboard.map((m, i) => (
                      <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2.5 pr-3">
                          <span className={`inline-block w-6 text-center font-bold ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-500" : i === 2 ? "text-orange-700" : "text-slate-400"}`}>
                            {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 font-medium text-slate-800">{m.name}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-500">₹{fmtL(m.target)}</td>
                        <td className="py-2.5 pr-3 text-right text-[#0066CC] font-semibold">₹{fmtL(m.collected)}</td>
                        <td className={`py-2.5 pr-3 text-right font-semibold flex items-center justify-end gap-1 ${m.achievement_pct >= 100 ? "text-emerald-600" : m.achievement_pct >= 50 ? "text-amber-600" : "text-red-600"}`}>
                          {m.achievement_pct >= 100 ? <TrendingUp className="w-3 h-3" /> : m.achievement_pct >= 50 ? null : <TrendingDown className="w-3 h-3" />}
                          {m.achievement_pct}%
                        </td>
                        <td className="py-2.5 pr-3 text-right text-slate-600">{m.ytd_efficiency}%</td>
                        <td className="py-2.5 pr-3 text-right text-slate-600">{m.share_pct}%</td>
                        <td className="py-2.5 pr-3 text-right text-slate-500">{m.receipt_count}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-500">₹{fmtL(m.avg_ticket)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}


/* ============================================================
   MonthlyDashboard — per-salesperson monthly targets & KPIs
   ============================================================ */
function ymDefault() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MonthlyKpi({ label, value, sub, color, icon: Icon }) {
  return (
    <Card><CardContent className="p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
          <p className="text-lg font-bold mt-0.5 truncate" style={{ color }}>{value}</p>
          {sub && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</p>}
        </div>
        {Icon && <div className="p-1.5 rounded-lg shrink-0" style={{ backgroundColor: `${color}15` }}><Icon className="w-3.5 h-3.5" style={{ color }} /></div>}
      </div>
    </CardContent></Card>
  );
}

function MemberCard({ member: m, data }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pct = m.achievement_pct;
  const barColor = pct >= 100 ? "#059669" : pct >= 75 ? "#0066CC" : pct >= 50 ? "#D97706" : "#DC2626";

  const takeSnapshot = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const CARD_WIDTH = 700; // fixed capture width for consistent, non-overlapping layout
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: CARD_WIDTH,
        width: CARD_WIDTH,
        letterRendering: true,
        ignoreElements: (el) => el.getAttribute && el.getAttribute("data-snapshot-hide") === "1",
        onclone: (doc) => {
          // html2canvas has a well-known bug where descenders (y, g, p, j) get clipped when
          // line-height is tight or when a parent has overflow:hidden. Fix by inflating line-height
          // globally, forcing overflow:visible everywhere, and disabling truncate/animations.
          doc.querySelectorAll("*").forEach((n) => {
            n.style.animation = "none";
            n.style.transition = "none";
            if (getComputedStyle(n).overflow === "hidden") n.style.overflow = "visible";
            // Bumping baseline line-height eliminates descender clipping across all text
            n.style.lineHeight = "1.55";
            // Kill "truncate" ellipsis (text-overflow + overflow:hidden + white-space:nowrap)
            if (n.classList && n.classList.contains("truncate")) {
              n.style.whiteSpace = "normal";
              n.style.textOverflow = "clip";
            }
          });
          const clonedCard = doc.querySelector(`[data-member-card="${m.id}"]`);
          if (clonedCard) {
            clonedCard.style.width = `${CARD_WIDTH - 40}px`;
            clonedCard.style.maxWidth = `${CARD_WIDTH - 40}px`;
            clonedCard.style.margin = "20px";
            clonedCard.style.overflow = "visible";
            // Extra vertical breathing room so the last row's descenders never touch the edge
            clonedCard.style.paddingBottom = "12px";
          }
        },
      });
      const link = document.createElement("a");
      const safeName = (m.name || "member").replace(/[^a-z0-9-]+/gi, "_").replace(/^_+|_+$/g, "");
      link.download = `${safeName}-${data.year_month}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success(`Downloaded ${link.download}`);
    } catch {
      toast.error("Snapshot failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card ref={cardRef} className="overflow-hidden" data-member-card={m.id} data-testid={`sales-monthly-card-${m.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold text-slate-900 truncate">{m.name}</p>
              {m.rank ? (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.rank === 1 ? "bg-amber-100 text-amber-700" : m.rank === 2 ? "bg-slate-100 text-slate-700" : m.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-blue-50 text-[#0066CC]"}`}>
                  <Award className="w-2.5 h-2.5 inline mr-0.5" /> #{m.rank}
                </span>
              ) : null}
            </div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Monthly target · ₹{fmtL(m.monthly_target)}</p>
          </div>
          <div className="flex items-start gap-2 shrink-0">
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: barColor }}>{pct}%</p>
              <p className="text-[10px] text-slate-400">Achievement</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={takeSnapshot}
              disabled={busy}
              data-snapshot-hide="1"
              data-testid={`sales-monthly-card-snapshot-${m.id}`}
              className="h-7 w-7 p-0 text-slate-500 hover:text-[#0066CC]"
              title="Download snapshot of this card (PNG)"
              aria-label="Snapshot card"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: barColor }} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <MonthlyKpi label="Collected" value={`₹${fmtL(m.collected)}`} sub={`${m.receipt_count} receipts`} color="#059669" icon={TrendingUp} />
          <MonthlyKpi label="Remaining" value={`₹${fmtL(m.remaining)}`} sub={data.is_current_month && data.days_remaining > 0 ? `${data.days_remaining} days left` : "Month closed"} color={m.remaining > 0 ? "#D97706" : "#94A3B8"} icon={Target} />
          <MonthlyKpi label="Daily Avg Needed" value={data.days_remaining > 0 ? `₹${fmtL(m.daily_avg_needed)}` : "—"} sub={data.days_remaining > 0 ? `to hit target` : "Month closed"} color="#0066CC" icon={Calendar} />
          <MonthlyKpi label="Streak" value={`${m.streak_days} day${m.streak_days === 1 ? "" : "s"}`} sub={m.streak_days > 0 ? "consecutive days at target" : "start a streak today"} color={m.streak_days > 0 ? "#7C3AED" : "#94A3B8"} icon={Flame} />
          <MonthlyKpi label="Follow-ups" value={`${m.pending_followups_count}`} sub="planned / partial entries" color="#0891B2" icon={ClipboardList} />
          <MonthlyKpi label="Outstanding" value={`₹${fmtL(m.outstanding_assigned)}`} sub="on assigned projects" color="#DC2626" icon={Wallet} />
        </div>

        {m.top_projects && m.top_projects.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Top {Math.min(5, m.top_projects.length)} projects this month</p>
            <div className="space-y-1">
              {m.top_projects.map((p, i) => (
                <div key={p.project_id + i} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`monthly-top-project-${m.id}-${i}`}>
                  <span className="text-slate-700 truncate min-w-0">
                    <span className="text-slate-400 mr-1">{i + 1}.</span>{p.project_name}
                  </span>
                  <span className="font-semibold text-slate-800 shrink-0">₹{fmtL(p.collected)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MonthlyDashboard() {
  const [ym, setYm] = useState(ymDefault);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null); // null = all, Array = filtered
  const [pdfBusy, setPdfBusy] = useState(false);
  const boardRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collections/sales-team/monthly-dashboard?year_month=${ym}`);
      setData(r.data);
      setSelectedIds(null); // reset filter when month changes
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load monthly dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ym]);

  // Build a picklist of last 12 months + current
  const months = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 15; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      arr.push({ key, label: d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) });
    }
    return arr;
  }, []);

  if (loading && !data) return <Card><CardContent className="p-12 flex flex-col items-center justify-center text-slate-400"><Loader2 className="w-8 h-8 animate-spin mb-2" /><p className="text-sm">Loading monthly dashboard…</p></CardContent></Card>;
  if (!data) return null;

  const activeMembersAll = (data.members || []).filter(m => m.active !== false);
  const isFiltered = selectedIds !== null;
  const activeMembers = isFiltered
    ? activeMembersAll.filter(m => selectedIds.includes(m.id))
    : activeMembersAll;

  // Recompute team totals from the filtered set
  const filteredTotals = (() => {
    if (!isFiltered) return data.totals;
    const target = activeMembers.reduce((s, m) => s + (m.monthly_target || 0), 0);
    const collected = activeMembers.reduce((s, m) => s + (m.collected || 0), 0);
    const ach = target > 0 ? Math.round((collected / target) * 1000) / 10 : 0;
    return { monthly_target: target, collected, achievement_pct: ach };
  })();

  const toggleMember = (id) => {
    setSelectedIds(prev => {
      if (prev === null) {
        // First tick — switch to explicit list, exclude the one clicked
        return activeMembersAll.filter(m => m.id !== id).map(m => m.id);
      }
      return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    });
  };
  const resetFilter = () => setSelectedIds(null);
  const selectOnly = (id) => setSelectedIds([id]);

  const downloadDashboardPdf = async () => {
    if (!boardRef.current || activeMembers.length === 0) return;
    setPdfBusy(true);
    try {
      const html2canvasMod = (await import("html2canvas")).default;
      const jsPDFMod = (await import("jspdf")).default;
      const el = boardRef.current;
      // Force a stable capture width so cards don't overlap on the canvas at odd viewport sizes
      const CAPTURE_WIDTH = 1400;
      const canvas = await html2canvasMod(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: CAPTURE_WIDTH,
        width: CAPTURE_WIDTH,
        ignoreElements: (n) => n.getAttribute && n.getAttribute("data-snapshot-hide") === "1",
        onclone: (doc) => {
          const root = doc.querySelector('[data-pdf-board="monthly"]');
          if (root) {
            root.style.padding = "20px";
            root.style.background = "#ffffff";
            root.style.width = `${CAPTURE_WIDTH}px`;
            root.style.maxWidth = `${CAPTURE_WIDTH}px`;
            root.style.overflow = "visible";
          }
          // Turn off animations, restore natural overflow, and fix html2canvas descender clipping
          doc.querySelectorAll("*").forEach((n) => {
            n.style.animation = "none";
            n.style.transition = "none";
            if (getComputedStyle(n).overflow === "hidden") n.style.overflow = "visible";
            n.style.lineHeight = "1.55";
            if (n.classList && n.classList.contains("truncate")) {
              n.style.whiteSpace = "normal";
              n.style.textOverflow = "clip";
            }
          });
        },
      });
      const imgW = canvas.width;
      const imgH = canvas.height;
      const pdf = new jsPDFMod({ orientation: imgW > imgH ? "landscape" : "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const scaledH = (imgH * pageW) / imgW;
      if (scaledH <= pageH) {
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageW, scaledH, undefined, "FAST");
      } else {
        let heightLeft = scaledH;
        let position = 0;
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, position, pageW, scaledH, undefined, "FAST");
        heightLeft -= pageH;
        while (heightLeft > 0) {
          position -= pageH;
          pdf.addPage();
          pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, position, pageW, scaledH, undefined, "FAST");
          heightLeft -= pageH;
        }
      }
      pdf.save(`sales-monthly-${data.year_month}${isFiltered ? "-filtered" : ""}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-monthly-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Calendar className="w-4 h-4" />
          <span>Showing metrics for</span>
          <select value={ym} onChange={e => setYm(e.target.value)} className="h-8 border border-slate-200 rounded-md px-2 text-xs bg-white" data-testid="sales-monthly-picker">
            {months.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          {data.is_current_month && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-50 text-[#0066CC] rounded">Day {data.day_of_month} of {data.days_in_month} · {data.days_remaining} left</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadDashboardPdf} disabled={pdfBusy || activeMembers.length === 0} data-testid="sales-monthly-pdf-btn">
            {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Salesperson multi-select filter */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-slate-700">Filter salespersons {isFiltered && <span className="ml-1 text-[10px] text-slate-500 font-normal">({activeMembers.length} of {activeMembersAll.length})</span>}</p>
            <div className="flex gap-1.5">
              {isFiltered && <button type="button" onClick={resetFilter} className="text-[10px] text-[#0066CC] hover:underline" data-testid="sales-monthly-reset-filter">Reset (All)</button>}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="sales-monthly-member-filter">
            {activeMembersAll.map(m => {
              const active = !isFiltered || selectedIds.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleMember(m.id)}
                  onDoubleClick={() => selectOnly(m.id)}
                  data-testid={`sales-monthly-member-toggle-${m.id}`}
                  className={`px-2.5 py-1 text-[11px] rounded-full border transition ${active ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Click to toggle. Double-click a name to isolate just that person. Totals below auto-refresh.</p>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-600 flex items-center gap-3 flex-wrap" data-testid="sales-monthly-team-totals">
        <span>Team Target: <strong className="text-slate-800">₹{fmtL(filteredTotals.monthly_target)}</strong></span>
        <span>Collected: <strong className="text-emerald-700">₹{fmtL(filteredTotals.collected)}</strong></span>
        <span>Achievement: <strong className={filteredTotals.achievement_pct >= 100 ? "text-emerald-700" : "text-slate-800"}>{filteredTotals.achievement_pct}%</strong></span>
      </div>

      <div ref={boardRef} data-pdf-board="monthly" className="space-y-4">
        {activeMembers.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-400"><AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-400" /><p className="text-sm">No members match the current filter.</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activeMembers.map(m => <MemberCard key={m.id} member={m} data={data} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================ */
/*  QuarterlyDashboard — 3-month rollup with same card layout    */
/* ============================================================ */
function QuarterlyDashboard() {
  // Indian FY: Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar
  const now = new Date();
  const _cy = now.getFullYear();
  const _cm = now.getMonth() + 1;
  const _currentFyStart = _cm >= 4 ? _cy : _cy - 1;
  const currentQ = _cm >= 4 && _cm <= 6 ? "Q1" : _cm >= 7 && _cm <= 9 ? "Q2" : _cm >= 10 && _cm <= 12 ? "Q3" : "Q4";
  const [fyYear, setFyYear] = useState(_currentFyStart);
  const [q, setQ] = useState(currentQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const boardRef = useRef(null);

  // Compute the 3 YYYY-MM month keys for a given FY & quarter
  const monthKeysFor = (fyStart, quarter) => {
    if (quarter === "Q1") return [`${fyStart}-04`, `${fyStart}-05`, `${fyStart}-06`];
    if (quarter === "Q2") return [`${fyStart}-07`, `${fyStart}-08`, `${fyStart}-09`];
    if (quarter === "Q3") return [`${fyStart}-10`, `${fyStart}-11`, `${fyStart}-12`];
    return [`${fyStart + 1}-01`, `${fyStart + 1}-02`, `${fyStart + 1}-03`];
  };

  const load = async () => {
    setLoading(true);
    try {
      const keys = monthKeysFor(fyYear, q);
      const responses = await Promise.all(keys.map(k => api.get(`/collections/sales-team/monthly-dashboard?year_month=${k}`)));
      const monthly = responses.map(r => r.data);
      // Aggregate across 3 months at the member level
      const memberAcc = {};
      for (const md of monthly) {
        for (const m of md.members || []) {
          if (!memberAcc[m.id]) {
            memberAcc[m.id] = {
              id: m.id, name: m.name, active: m.active,
              monthly_target: 0,       // will be the quarterly target
              collected: 0,
              receipt_count: 0,
              pending_followups_count: m.pending_followups_count || 0,
              outstanding_assigned: m.outstanding_assigned || 0,
              streak_days: m.streak_days || 0,
              by_project: {},
            };
          }
          const acc = memberAcc[m.id];
          acc.monthly_target += (m.monthly_target || 0);
          acc.collected += (m.collected || 0);
          acc.receipt_count += (m.receipt_count || 0);
          for (const p of (m.top_projects || [])) {
            const key = `${p.project_id || ""}||${p.project_name}`;
            acc.by_project[key] = (acc.by_project[key] || { project_id: p.project_id, project_name: p.project_name, collected: 0 });
            acc.by_project[key].collected += (p.collected || 0);
          }
        }
      }
      const members = Object.values(memberAcc).map(m => {
        const remaining = Math.max(0, m.monthly_target - m.collected);
        const pct = m.monthly_target > 0 ? Math.round((m.collected / m.monthly_target) * 1000) / 10 : 0;
        const topProjects = Object.values(m.by_project)
          .filter(p => p.collected > 0)
          .sort((a, b) => b.collected - a.collected)
          .slice(0, 5);
        return {
          ...m, remaining, achievement_pct: pct,
          daily_avg_needed: 0, // Not meaningful over 3 months — omit
          top_projects: topProjects,
        };
      });
      // Rank members by collected
      members.sort((a, b) => b.collected - a.collected);
      members.forEach((m, i) => { m.rank = m.collected > 0 ? i + 1 : null; });
      const totalTarget = members.reduce((s, m) => s + m.monthly_target, 0);
      const totalCollected = members.reduce((s, m) => s + m.collected, 0);
      const totalPct = totalTarget > 0 ? Math.round((totalCollected / totalTarget) * 1000) / 10 : 0;
      const label = `${q} FY ${String(fyYear).slice(-2)}-${String(fyYear + 1).slice(-2)}`;
      setData({
        quarter_label: label,
        year_month: `${keys[0]}_to_${keys[2]}`,
        is_current_month: false,
        day_of_month: 0, days_in_month: 0, days_remaining: 0,
        members,
        totals: { monthly_target: totalTarget, collected: totalCollected, achievement_pct: totalPct },
      });
      setSelectedIds(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load quarterly dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyYear, q]);

  if (loading && !data) return <Card><CardContent className="p-12 flex flex-col items-center justify-center text-slate-400"><Loader2 className="w-8 h-8 animate-spin mb-2" /><p className="text-sm">Loading quarterly dashboard…</p></CardContent></Card>;
  if (!data) return null;

  const activeMembersAll = (data.members || []).filter(m => m.active !== false);
  const isFiltered = selectedIds !== null;
  const activeMembers = isFiltered ? activeMembersAll.filter(m => selectedIds.includes(m.id)) : activeMembersAll;
  const filteredTotals = (() => {
    if (!isFiltered) return data.totals;
    const target = activeMembers.reduce((s, m) => s + (m.monthly_target || 0), 0);
    const collected = activeMembers.reduce((s, m) => s + (m.collected || 0), 0);
    const ach = target > 0 ? Math.round((collected / target) * 1000) / 10 : 0;
    return { monthly_target: target, collected, achievement_pct: ach };
  })();

  const toggleMember = (id) => setSelectedIds(prev => {
    if (prev === null) return activeMembersAll.filter(m => m.id !== id).map(m => m.id);
    return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
  });
  const resetFilter = () => setSelectedIds(null);
  const selectOnly = (id) => setSelectedIds([id]);

  const downloadDashboardPdf = async () => {
    if (!boardRef.current || activeMembers.length === 0) return;
    setPdfBusy(true);
    try {
      const html2canvasMod = (await import("html2canvas")).default;
      const jsPDFMod = (await import("jspdf")).default;
      const CAPTURE_WIDTH = 1400;
      const canvas = await html2canvasMod(boardRef.current, {
        backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false,
        windowWidth: CAPTURE_WIDTH, width: CAPTURE_WIDTH,
        ignoreElements: (n) => n.getAttribute && n.getAttribute("data-snapshot-hide") === "1",
        onclone: (doc) => {
          const root = doc.querySelector('[data-pdf-board="quarterly"]');
          if (root) {
            root.style.padding = "20px";
            root.style.background = "#ffffff";
            root.style.width = `${CAPTURE_WIDTH}px`;
            root.style.maxWidth = `${CAPTURE_WIDTH}px`;
            root.style.overflow = "visible";
          }
          doc.querySelectorAll("*").forEach((n) => {
            n.style.animation = "none";
            n.style.transition = "none";
            if (getComputedStyle(n).overflow === "hidden") n.style.overflow = "visible";
            n.style.lineHeight = "1.55";
            if (n.classList && n.classList.contains("truncate")) {
              n.style.whiteSpace = "normal";
              n.style.textOverflow = "clip";
            }
          });
        },
      });
      const imgW = canvas.width, imgH = canvas.height;
      const pdf = new jsPDFMod({ orientation: imgW > imgH ? "landscape" : "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const scaledH = (imgH * pageW) / imgW;
      if (scaledH <= pageH) {
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageW, scaledH, undefined, "FAST");
      } else {
        let heightLeft = scaledH; let position = 0;
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, position, pageW, scaledH, undefined, "FAST");
        heightLeft -= pageH;
        while (heightLeft > 0) {
          position -= pageH; pdf.addPage();
          pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, position, pageW, scaledH, undefined, "FAST");
          heightLeft -= pageH;
        }
      }
      pdf.save(`sales-quarterly-${data.year_month}${isFiltered ? "-filtered" : ""}.pdf`);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF generation failed");
    } finally { setPdfBusy(false); }
  };

  const fyOptions = [_currentFyStart - 2, _currentFyStart - 1, _currentFyStart, _currentFyStart + 1];

  return (
    <div className="space-y-4" data-testid="sales-quarterly-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Calendar className="w-4 h-4" />
          <span>Quarter</span>
          <select value={q} onChange={e => setQ(e.target.value)} className="h-8 border border-slate-200 rounded-md px-2 text-xs bg-white" data-testid="sales-quarterly-q-picker">
            {["Q1", "Q2", "Q3", "Q4"].map(qq => <option key={qq} value={qq}>{qq}</option>)}
          </select>
          <span>of</span>
          <select value={fyYear} onChange={e => setFyYear(Number(e.target.value))} className="h-8 border border-slate-200 rounded-md px-2 text-xs bg-white" data-testid="sales-quarterly-fy-picker">
            {fyOptions.map(y => <option key={y} value={y}>FY {String(y).slice(-2)}-{String(y + 1).slice(-2)}</option>)}
          </select>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-50 text-[#0066CC] rounded">{data.quarter_label}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadDashboardPdf} disabled={pdfBusy || activeMembers.length === 0} data-testid="sales-quarterly-pdf-btn">
            {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />}
            Download PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-slate-700">Filter salespersons {isFiltered && <span className="ml-1 text-[10px] text-slate-500 font-normal">({activeMembers.length} of {activeMembersAll.length})</span>}</p>
            <div className="flex gap-1.5">
              {isFiltered && <button type="button" onClick={resetFilter} className="text-[10px] text-[#0066CC] hover:underline" data-testid="sales-quarterly-reset-filter">Reset (All)</button>}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="sales-quarterly-member-filter">
            {activeMembersAll.map(m => {
              const active = !isFiltered || selectedIds.includes(m.id);
              return (
                <button key={m.id} type="button" onClick={() => toggleMember(m.id)} onDoubleClick={() => selectOnly(m.id)}
                  data-testid={`sales-quarterly-member-toggle-${m.id}`}
                  className={`px-2.5 py-1 text-[11px] rounded-full border transition ${active ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
                  {m.name}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-600 flex items-center gap-3 flex-wrap" data-testid="sales-quarterly-team-totals">
        <span>Team Target: <strong className="text-slate-800">₹{fmtL(filteredTotals.monthly_target)}</strong></span>
        <span>Collected: <strong className="text-emerald-700">₹{fmtL(filteredTotals.collected)}</strong></span>
        <span>Achievement: <strong className={filteredTotals.achievement_pct >= 100 ? "text-emerald-700" : "text-slate-800"}>{filteredTotals.achievement_pct}%</strong></span>
      </div>

      <div ref={boardRef} data-pdf-board="quarterly" className="space-y-4">
        {activeMembers.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-400"><AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-400" /><p className="text-sm">No members match the current filter.</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activeMembers.map(m => <MemberCard key={m.id} member={m} data={data} />)}
          </div>
        )}
      </div>
    </div>
  );
}
